import React, { useState, useEffect, useMemo } from 'react';
import { roleAPI, permissionAPI } from '../../utils/api';
import Swal from 'sweetalert2';
import {
  FaShieldAlt, FaSave, FaCheckSquare, FaSquare, FaSearch,
  FaChevronDown, FaChevronUp, FaMinus, FaTh, FaList
} from 'react-icons/fa';

export default function RolePermissionsPage() {
  const [roles, setRoles]           = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [groupedPerms, setGroupedPerms]     = useState({});
  const [checked, setChecked]       = useState({});
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [search, setSearch]         = useState('');
  const [collapsed, setCollapsed]   = useState({});
  
  // Tab configuration
  const [activeTab, setActiveTab]   = useState('standard'); // 'standard' or 'matrix'
  const [matrixChecked, setMatrixChecked] = useState({});
  const [matrixLoading, setMatrixLoading] = useState(false);

  useEffect(() => {
    async function loadRoles() {
      try {
        const r = await roleAPI.getRoles();
        const list = Array.isArray(r.data) ? r.data : (r.data.content || []);
        setRoles(list);
        if (list.length) setSelectedRoleId(list[0].id);
      } catch (e) {
        Swal.fire('Error', e.message, 'error');
      }
    }
    loadRoles();
  }, []);

  useEffect(() => {
    if (!selectedRoleId) return;
    async function loadPerms() {
      setLoading(true);
      try {
        const [groupedRes, roleRes] = await Promise.all([
          permissionAPI.getPermissionsGrouped(),
          roleAPI.getRole(selectedRoleId),
        ]);
        setGroupedPerms(groupedRes.data);

        // Sequelize returns association as 'Permissions' (capital P)
        const rolePermIds = (roleRes.data.Permissions || roleRes.data.permissions || []).map(p => String(p.id));
        const initial = {};
        Object.values(groupedRes.data).forEach(subMap => {
          Object.values(subMap).forEach(list => {
            list.forEach(p => { initial[p.id] = rolePermIds.includes(p.id); });
          });
        });
        setChecked(initial);
      } catch (e) {
        Swal.fire('Error', e.message, 'error');
      } finally {
        setLoading(false);
      }
    }
    loadPerms();
  }, [selectedRoleId]);

  const loadMatrixData = async () => {
    setMatrixLoading(true);
    try {
      // Ensure we have permissions fetched
      let activeGrouped = groupedPerms;
      if (Object.keys(activeGrouped).length === 0) {
        const groupedRes = await permissionAPI.getPermissionsGrouped();
        setGroupedPerms(groupedRes.data);
        activeGrouped = groupedRes.data;
      }

      // Fetch details for all roles in parallel
      const detailed = await Promise.all(roles.map(r => roleAPI.getRole(r.id)));
      const initial = {};
      detailed.forEach(roleRes => {
        const roleId = roleRes.data.id;
        const rolePermIds = (roleRes.data.Permissions || roleRes.data.permissions || []).map(p => String(p.id));
        initial[roleId] = {};
        
        Object.values(activeGrouped).forEach(subMap => {
          Object.values(subMap).forEach(list => {
            list.forEach(p => {
              initial[roleId][p.id] = rolePermIds.includes(String(p.id));
            });
          });
        });
      });
      setMatrixChecked(initial);
    } catch (e) {
      Swal.fire('Error Loading Matrix', e.message, 'error');
    } finally {
      setMatrixLoading(false);
    }
  };

  const toggle = id => setChecked(prev => ({ ...prev, [id]: !prev[id] }));

  const handleMatrixToggle = async (roleId, permId) => {
    const nextVal = !matrixChecked[roleId]?.[permId];
    
    // Update local state instantly for extreme responsiveness
    setMatrixChecked(prev => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        [permId]: nextVal
      }
    }));

    try {
      const rolePerms = { ...matrixChecked[roleId], [permId]: nextVal };
      const ids = Object.entries(rolePerms)
        .filter(([, v]) => v)
        .map(([id]) => Number(id));

      await roleAPI.updateRolePermissions(roleId, ids);
      
      Swal.fire({
        title: 'Updated',
        text: 'Permissions synchronized successfully!',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
    } catch (e) {
      // Revert state on failure
      setMatrixChecked(prev => ({
        ...prev,
        [roleId]: {
          ...prev[roleId],
          [permId]: !nextVal
        }
      }));
      Swal.fire('Sync Error', e.message, 'error');
    }
  };

  /* All permissions in a module (flat) */
  const modulePerms = mod => {
    const subMap = groupedPerms[mod] || {};
    return Object.values(subMap).flat();
  };

  /* Check state for a module: 'all' | 'some' | 'none' */
  const moduleState = mod => {
    const perms = modulePerms(mod).filter(p => matchesSearch(p));
    if (!perms.length) return 'none';
    const checkedCount = perms.filter(p => checked[p.id]).length;
    if (checkedCount === 0) return 'none';
    if (checkedCount === perms.length) return 'all';
    return 'some';
  };

  const toggleModule = mod => {
    const perms = modulePerms(mod).filter(p => matchesSearch(p));
    const state = moduleState(mod);
    const newVal = state !== 'all';
    setChecked(prev => {
      const next = { ...prev };
      perms.forEach(p => { next[p.id] = newVal; });
      return next;
    });
  };

  const matchesSearch = p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
               p.description?.toLowerCase().includes(search.toLowerCase());

  const totalPerms   = Object.keys(checked).length;
  const checkedCount = Object.values(checked).filter(Boolean).length;

  const save = async () => {
    setSaving(true);
    try {
      const ids = Object.entries(checked).filter(([, v]) => v).map(([id]) => Number(id));
      await roleAPI.updateRolePermissions(selectedRoleId, ids);
      Swal.fire({ title: 'Saved', text: 'Role permissions updated', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const selectedRole = useMemo(() => roles.find(r => r.id === selectedRoleId), [roles, selectedRoleId]);
  const pct = totalPerms > 0 ? Math.round((checkedCount / totalPerms) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '22px 28px', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaShieldAlt style={{ color: '#0f6fad' }} /> Role Entitlements
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
              Configure the access matrix for each system role under ERP Governance standards
            </p>
          </div>
          
          {activeTab === 'standard' && (
            <button onClick={save} disabled={saving || loading} style={primaryBtn}>
              <FaSave /> {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>

        {/* Unified Tab Selector */}
        <div style={{ display: 'flex', gap: 12, padding: '0 28px', borderBottom: '1px solid #f0f4f8' }}>
          <button 
            onClick={() => setActiveTab('standard')}
            style={{
              ...tabItemStyle,
              borderBottomColor: activeTab === 'standard' ? '#10ac84' : 'transparent',
              color: activeTab === 'standard' ? '#10ac84' : '#64748b',
              fontWeight: activeTab === 'standard' ? 700 : 500
            }}
          >
            <FaList /> Standard Card Configurator
          </button>
          <button 
            onClick={() => { setActiveTab('matrix'); loadMatrixData(); }}
            style={{
              ...tabItemStyle,
              borderBottomColor: activeTab === 'matrix' ? '#10ac84' : 'transparent',
              color: activeTab === 'matrix' ? '#10ac84' : '#64748b',
              fontWeight: activeTab === 'matrix' ? 700 : 500
            }}
          >
            <FaTh /> Unified Visual Matrix Grid
          </button>
        </div>

        {/* Standard-only search panel */}
        {activeTab === 'standard' && (
          <div style={{ padding: '20px 28px 22px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 220 }}>
              <label style={labelStyle}>Select Role</label>
              <select
                value={selectedRoleId || ''}
                onChange={e => setSelectedRoleId(Number(e.target.value))}
                style={selectStyle}
              >
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name.replace('ROLE_', '')}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Search Permissions</label>
              <div style={searchBoxStyle}>
                <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem', marginRight: 8, flexShrink: 0 }} />
                <input
                  type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Filter by name or description…"
                  style={searchInputStyle}
                />
              </div>
            </div>

            {!loading && totalPerms > 0 && (
              <div style={{ minWidth: 180 }}>
                <label style={labelStyle}>Assigned</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, height: 8, background: '#f0f4f8', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#10ac84' : pct > 50 ? '#0f6fad' : '#f39c12', borderRadius: 4, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>
                    {checkedCount} / {totalPerms} ({pct}%)
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* RENDER ACTIVE VIEW */}
      {activeTab === 'standard' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {loading
            ? [...Array(6)].map((_, i) => <div key={i} style={{ height: 160, borderRadius: 14, background: '#f0f4f8', animation: 'dtShimmer 1.4s infinite' }} />)
            : Object.entries(groupedPerms).map(([module, subMap]) => {
                const allPerms = Object.values(subMap).flat().filter(matchesSearch);
                if (!allPerms.length) return null;

                const state = moduleState(module);
                const isOpen = !collapsed[module];

                return (
                  <div key={module} style={moduleCardStyle}>
                    {/* Module header */}
                    <div
                      style={moduleHeaderStyle}
                      onClick={() => setCollapsed(p => ({ ...p, [module]: !p[module] }))}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button
                          onClick={e => { e.stopPropagation(); toggleModule(module); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#fff', display: 'flex', alignItems: 'center', fontSize: '1rem' }}
                          title={state === 'all' ? 'Deselect all' : 'Select all'}
                        >
                          {state === 'all'
                            ? <FaCheckSquare />
                            : state === 'some'
                              ? <FaMinus style={{ opacity: 0.7 }} />
                              : <FaSquare style={{ opacity: 0.5 }} />}
                        </button>
                        <span style={{ fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                          {module}
                        </span>
                        <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 600 }}>
                          {allPerms.filter(p => checked[p.id]).length}/{allPerms.length}
                        </span>
                      </div>
                      {isOpen ? <FaChevronUp style={{ fontSize: '0.75rem', opacity: 0.7 }} /> : <FaChevronDown style={{ fontSize: '0.75rem', opacity: 0.7 }} />}
                    </div>

                    {/* Permissions list */}
                    {isOpen && (
                      <div style={{ padding: '12px' }}>
                        {Object.entries(subMap).map(([sub, list]) => {
                          const visible = list.filter(matchesSearch);
                          if (!visible.length) return null;
                          return (
                            <div key={sub} style={{ marginBottom: 12 }}>
                              {Object.keys(subMap).length > 1 && (
                                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid #f0f4f8' }}>
                                  {sub}
                                </div>
                              )}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                {visible.map(p => (
                                  <div
                                    key={p.id}
                                    onClick={() => toggle(p.id)}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 8,
                                      padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                                      background: checked[p.id] ? 'rgba(15,111,173,0.06)' : 'transparent',
                                      border: `1px solid ${checked[p.id] ? 'rgba(15,111,173,0.15)' : 'transparent'}`,
                                      transition: 'all 0.15s',
                                    }}
                                  >
                                    <span style={{ color: checked[p.id] ? '#0f6fad' : '#cbd5e1', flexShrink: 0, fontSize: '0.85rem' }}>
                                      {checked[p.id] ? <FaCheckSquare /> : <FaSquare />}
                                    </span>
                                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: checked[p.id] ? '#1e293b' : '#64748b', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                      {(p.name || '').replace(/_/g, ' ')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
          }
        </div>
      ) : (
        /* SPECTACULAR UNIFIED VISUAL MATRIX VIEW */
        <div style={cardStyle}>
          {matrixLoading ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>
              <div style={{ width: 40, height: 40, border: '4px solid #cbd5e1', borderTopColor: '#10ac84', borderRadius: '50%', animation: 'dtShimmer 1.4s infinite', margin: '0 auto 16px' }} />
              <span>Analyzing global permissions and synthesizing real-time visual matrix...</span>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table style={matrixTableStyle}>
                <thead>
                  <tr>
                    <th style={matrixHeadingStyle}>Permission Module / Entitlement Details</th>
                    {roles.map(r => (
                      <th key={r.id} style={matrixRoleHeadingStyle}>
                        <div style={{ textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                          {r.name.replace('ROLE_', '').replace(/_/g, ' ')}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedPerms).map(([module, subMap]) => {
                    const flat = Object.values(subMap).flat();
                    return (
                      <React.Fragment key={module}>
                        {/* Module Separator Row */}
                        <tr style={{ background: '#f8fafc' }}>
                          <td colSpan={roles.length + 1} style={matrixModuleRowStyle}>
                            <FaShieldAlt style={{ color: '#0f6fad', marginRight: 8 }} />
                            <span>{module.toUpperCase()} ENTITLEMENTS</span>
                            <span style={{ marginLeft: 8, background: 'rgba(15,111,173,0.1)', color: '#0f6fad', borderRadius: 20, padding: '2px 8px', fontSize: '0.68rem', fontWeight: 700 }}>
                              {flat.length} Keys
                            </span>
                          </td>
                        </tr>
                        {flat.map(p => (
                          <tr key={p.id} style={matrixRowStyle}>
                            <td style={matrixPermCellStyle}>
                              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                {p.name.replace(/_/g, ' ')}
                              </div>
                              {p.description && (
                                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>
                                  {p.description}
                                </div>
                              )}
                            </td>
                            {roles.map(r => {
                              const isChecked = !!matrixChecked[r.id]?.[p.id];
                              return (
                                <td key={r.id} style={matrixCheckCellStyle} onClick={() => handleMatrixToggle(r.id, p.id)}>
                                  <div style={{
                                    fontSize: '1.1rem', cursor: 'pointer',
                                    color: isChecked ? '#10ac84' : '#cbd5e1',
                                    transition: 'color 0.15s ease'
                                  }}>
                                    {isChecked ? <FaCheckSquare /> : <FaSquare />}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Floating save bar standard view */}
      {activeTab === 'standard' && !loading && totalPerms > 0 && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 500,
          background: '#1e293b', color: '#fff',
          borderRadius: 12, padding: '12px 20px',
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
        }}>
          <span style={{ fontSize: '0.85rem' }}>
            <strong>{selectedRole?.name?.replace('ROLE_', '')}</strong>
            {' '}— {checkedCount} permissions selected
          </span>
          <button onClick={save} disabled={saving} style={{ ...primaryBtn, padding: '8px 16px', fontSize: '0.82rem', boxShadow: 'none' }}>
            <FaSave /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      <style>{`
        @keyframes dtShimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

/* ---- Style constants ---- */
const cardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #f0f4f8', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' };
const moduleCardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #f0f4f8', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' };
const moduleHeaderStyle = {
  background: 'linear-gradient(135deg, #0f6fad, #16a085)',
  color: '#fff', padding: '12px 16px',
  display: 'flex', alignItems: 'center', justifySelf: 'stretch', justifyContent: 'space-between',
  cursor: 'pointer', userSelect: 'none',
};
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #10ac84, #0e9a75)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins',sans-serif", boxShadow: '0 2px 8px rgba(16,172,132,0.25)' };
const selectStyle = { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#f8fafc', fontSize: '0.875rem', color: '#374151', outline: 'none', fontFamily: "'Poppins',sans-serif", cursor: 'pointer' };
const searchBoxStyle = { display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 12px', height: 42 };
const searchInputStyle = { flex: 1, border: 'none', background: 'transparent', fontSize: '0.875rem', color: '#374151', outline: 'none', fontFamily: "'Poppins',sans-serif" };
const labelStyle = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' };

const tabItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '16px 20px',
  border: 'none',
  background: 'none',
  borderBottom: '3px solid transparent',
  fontSize: '0.88rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  outline: 'none',
  fontFamily: "'Poppins', sans-serif"
};

/* Matrix Table Styles */
const matrixTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  textAlign: 'left'
};

const matrixHeadingStyle = {
  padding: '16px 24px',
  background: '#f8fafc',
  borderBottom: '2px solid #e2e8f0',
  color: '#475569',
  fontWeight: 700,
  fontSize: '0.82rem',
  textTransform: 'uppercase',
  letterSpacing: '0.6px'
};

const matrixRoleHeadingStyle = {
  padding: '16px 12px',
  background: '#f8fafc',
  borderBottom: '2px solid #e2e8f0',
  color: '#0f6fad',
  fontWeight: 700,
  textAlign: 'center',
  minWidth: 120
};

const matrixModuleRowStyle = {
  padding: '12px 24px',
  fontWeight: 700,
  fontSize: '0.78rem',
  color: '#334155',
  borderBottom: '1px solid #e2e8f0',
  letterSpacing: '0.5px'
};

const matrixRowStyle = {
  borderBottom: '1px solid #f1f5f9',
  transition: 'background 0.15s ease'
};

const matrixPermCellStyle = {
  padding: '14px 24px',
  maxWidth: 360
};

const matrixCheckCellStyle = {
  padding: '14px 12px',
  textAlign: 'center',
  verticalAlign: 'middle',
  cursor: 'pointer'
};

